import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { AdminContext } from '../../contexts/admin.context';

import { FloatingBackButton } from '../../components/common';
import StepBar from '../../components/common/StepBar';
import ButtonNext from '../../components/ui/button/ButtonNext';

import { useTranslation } from 'react-i18next';
import { getTranslation } from '../../util/translation';

import {
  StarIcon,
  HexagonIcon,
  MagnifierIcon,
  HeadIcon,
  PersonIcon,
  RadarIcon
} from '../../components/common/index';

const getInitials = (name = '') => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const iconsMap = [
  [MagnifierIcon, HeadIcon],
  [MagnifierIcon, PersonIcon],
  [RadarIcon, HeadIcon],
  [RadarIcon, PersonIcon]
];

const Symbol = ({ name, type, filled = false }) => {
  const initials = getInitials(name);
  return (
    <span className={`relative w-10 h-10 flex items-center justify-center`}>
      <span className="w-full h-full absolute top-0 left-0">
        {type === 'a' ? <StarIcon filled={filled} /> : <HexagonIcon filled={filled} />}
      </span>
      <span className={`relative text-xs text-white uppercase`}>{initials}</span>
    </span>
  );
};

export default function StudentProfileCommPreferences({ type = 'a' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    portalConfig: { basePath } = {},
    studentProfile,
    setStudentProfile
  } = useContext(AdminContext);

  const [options, setOptions] = useState([[]]);
  const [communicationPreferences, setCommunicationPreferences] = useState({ a: {}, b: {} });
  const [totalSelections, setTotalSelections] = useState(0);

  useEffect(() => {
    const translatedOptions = t('resources.starsList', { returnObjects: true });
    if (Array.isArray(translatedOptions) && translatedOptions.length > 0) {
      setOptions(translatedOptions);
    } else {
      console.warn('Failed to load star options from translations.');
      setOptions([
        { optionOne: 'Option 1.1', optionTwo: 'Option 1.2' },
        { optionOne: 'Option 2.1', optionTwo: 'Option 2.2' },
        { optionOne: 'Option 3.1', optionTwo: 'Option 3.2' },
        { optionOne: 'Option 4.1', optionTwo: 'Option 4.2' }
      ]);
    }
  }, [t]);

  useEffect(() => {
    if (isEmpty(studentProfile.roles)) {
      navigate(basePath + '/student-profile-3');
      return;
    }

    if (studentProfile.communicationPreferences) {
      setCommunicationPreferences(studentProfile.communicationPreferences);
    } else {
      setCommunicationPreferences({ a: {}, b: {} });
    }

    let initialSelections = 0;
    const currentTypePrefs = studentProfile.communicationPreferences?.[type] || {};
    Object.keys(currentTypePrefs).forEach((index) => {
      Object.keys(currentTypePrefs[index]).forEach((key) => {
        if (currentTypePrefs[index][key]?.some((user) => user?.id === studentProfile?.id)) {
          initialSelections++;
        }
      });
    });
    setTotalSelections(initialSelections);

    return () => {
      setTotalSelections(0);
    };
  }, [studentProfile, type, navigate, basePath]);

  const onContinue = () => {
    setStudentProfile({ ...studentProfile, communicationPreferences });
    navigate(basePath + (type === 'a' ? '/student-profile-5' : '/student-profile-6'));
  };

  const onGoBack = () => {
    navigate(basePath + (type === 'a' ? '/student-profile-3' : '/student-profile-4'));
  };

  const canContinue = totalSelections === 2;
  const userName = `${studentProfile?.firstname || ''}`.trim();
  const userId = studentProfile?.id;

  return (
    <section className="pb-20">
      <FloatingBackButton className="block" onGoBack={onGoBack} />
      <div className="max-w-7xl mx-auto">
        <div className="max-w-md mx-auto">
          <StepBar className="w-full my-8" activeStep={type === 'a' ? 4 : 5} />
        </div>
        <h2
          className="text-xl md:text-2xl font-semibold text-black-default text-center"
          dangerouslySetInnerHTML={{
            __html: getTranslation(
              t,
              `class.student.student_login_data.page_${type === 'a' ? 5 : 6}.step_${
                type === 'a' ? 5 : 6
              }`
            )
          }}
        />
        <p
          className="text-sm md:text-lg text-cblack-default text-center"
          dangerouslySetInnerHTML={{
            __html: getTranslation(
              t,
              `class.student.student_login_data.page_${type === 'a' ? 5 : 6}.description`
            )
          }}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 my-4">
          {[0, 1, 2, 3].map((index) => {
            const sectionOptions = options[index] || {};
            return (
              <div
                key={index}
                className="relative bg-white p-4 rounded-2xl border border-cgrey-300 flex flex-col">
                <div className="relative w-full h-40 md:h-48 rounded-lg mb-3 overflow-hidden">
                  <img
                    src={require(`../../assets/images/maps/${index}.png`)}
                    className="block relative w-full h-full object-cover"
                    alt={`Map ${index + 1}`}
                  />
                  <div className="absolute right-2 bottom-2 flex flex-row gap-2">
                    {iconsMap[index] &&
                      iconsMap[index].map((IconComponent, iconIndex) => (
                        <span
                          key={iconIndex}
                          className="w-10 h-10 bg-[#EDEDED] rounded-full p-2 flex items-center justify-center">
                          <IconComponent bg={'#EDEDED'} iconColor={'#3E3E3E'} />
                        </span>
                      ))}
                  </div>
                </div>

                <div className="flex flex-col gap-3 flex-grow">
                  <p
                    className="text-lg text-orange-500 font-bold w-full text-left whitespace-break-spaces"
                    dangerouslySetInnerHTML={{
                      __html: getTranslation(t, `starPicker.listHeading`, true, index, '', true)
                    }}
                  />
                  {Object.keys(sectionOptions).map((key, optionsIndex) => {
                    const text = sectionOptions[key];
                    // const isSelectedA = communicationPreferences?.['a']?.[index]?.[key]?.some(
                    //   (user) => user?.id === userId
                    // );
                    const isSelected = communicationPreferences?.[type]?.[index]?.[key]?.some(
                      (user) => user?.id === userId
                    );

                    return (
                      <div
                        key={optionsIndex}
                        className="flex flex-row gap-2 items-start justify-between my-1">
                        <span
                          className="relative cursor-pointer w-full flex gap-2 flex-row flex-wrap"
                          onClick={() => {
                            if (!userId || !userName) return;

                            const currentSelections =
                              communicationPreferences?.[type]?.[index]?.[key] || [];
                            let updatedPrefs = { ...communicationPreferences };
                            let newTotalSelections = totalSelections;

                            if (isSelected) {
                              updatedPrefs[type][index][key] = currentSelections.filter(
                                (user) => user.id !== userId
                              );
                              newTotalSelections--;
                            } else if (totalSelections < 2) {
                              if (!updatedPrefs[type]) updatedPrefs[type] = {};
                              if (!updatedPrefs[type][index]) updatedPrefs[type][index] = {};
                              if (!updatedPrefs[type][index][key])
                                updatedPrefs[type][index][key] = [];

                              updatedPrefs[type][index][key] = [
                                ...currentSelections,
                                { id: userId, name: userName, type }
                              ];
                              newTotalSelections++;
                            } else {
                              console.log('Maximum selections reached');
                              return;
                            }

                            setCommunicationPreferences(updatedPrefs);
                            setTotalSelections(newTotalSelections);
                          }}>
                          <p
                            className="text-gray-700 wrapper w-3/5 whitespace-break-spaces text-sm flex-grow mr-2"
                            dangerouslySetInnerHTML={{
                              __html: text || `Option ${optionsIndex + 1}`
                            }}
                          />
                          {/* {type === 'b' && isSelectedA && (
                            <Symbol type="a" name={userName} filled={true} />
                          )} */}
                          <Symbol type={type} name={userName} filled={isSelected} />
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <ButtonNext
            title={getTranslation(
              t,
              `class.student.student_login_data.page_${type === 'a' ? 5 : 6}.button_content`
            )}
            className="mx-auto"
            handler={onContinue}
            disabled={!canContinue}
          />
          {!canContinue && (
            <p className="text-sm text-red-500 mt-2">
              {t('communicationPreferences.selectionRequirement')}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
